import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { PaymentsStore, PaymentsProviderState } from '../shared/types/provider';
import { PaymentsConfig } from '../shared/types/config';
import { PaymentErrorType, PaymentsError } from '../shared/types/errors';
import { Customer, CustomerCreateParams } from '../shared/types/customer';
import {
  Subscription,
  SubscriptionCreateParams,
  SubscriptionUpdateParams,
  SubscriptionStatus,
} from '../shared/types/subscription';
import { CheckoutParams, CheckoutSession } from '../shared/types/checkout';
import { PaymentProviderFactory } from '../providers';
import { PaymentProvider } from '../shared/types/provider';

const initialState: PaymentsProviderState = {
  customer: null,
  subscriptions: [],
  activeSubscription: null,
  loading: false,
  error: null,
  initialized: false,
  config: null, // Add config to the state
  // Backward compatibility getters (will be computed)
  isLoading: false,
  isError: false,
};

export const usePayments = create<PaymentsStore>()(
  immer((set, get) => ({
    ...initialState,

    // Override getters to always return computed values
    get isLoading() {
      return get().loading;
    },
    get isError() {
      return !!get().error;
    },

    initialize: async (config?: PaymentsConfig) => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        let resolvedConfig: PaymentsConfig;
        if (config) {
          resolvedConfig = config;
        } else {
          resolvedConfig = PaymentProviderFactory.autodetect();
        }

        const provider: PaymentProvider =
          PaymentProviderFactory.create(resolvedConfig);
        await provider.initialize();

        set((state: PaymentsProviderState) => {
          state.initialized = true;
          state.loading = false;
          state.config = resolvedConfig; // Store the resolved config in the state
        });
      } catch (error: unknown) {
        set(state => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to initialize payments provider.';
          state.error = new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    createCustomer: async (params: CustomerCreateParams): Promise<Customer> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const customer = await provider.createCustomer(params);
        set((state: PaymentsProviderState) => {
          state.customer = customer;
          state.loading = false;
        });
        return customer;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to create customer.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    retrieveCustomer: async (customerId: string): Promise<Customer | null> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const customer = await provider.retrieveCustomer(customerId);
        set((state: PaymentsProviderState) => {
          state.customer = customer;
          state.loading = false;
        });
        return customer;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to retrieve customer.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    createCheckoutSession: async (
      params: CheckoutParams
    ): Promise<CheckoutSession> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const session = await provider.createCheckoutSession(params);
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
        return session;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to create checkout session.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    retrieveCheckoutSession: async (
      sessionId: string
    ): Promise<CheckoutSession> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const session = await provider.retrieveCheckoutSession(sessionId);
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
        return session;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to retrieve checkout session.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    createSubscription: async (
      params: SubscriptionCreateParams
    ): Promise<Subscription> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const newSubscription = await provider.createSubscription(params);
        await get().refreshSubscriptions(get().customer?.id || '');
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
        return newSubscription;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to create subscription.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    cancelSubscription: async (subscriptionId: string): Promise<void> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        await provider.cancelSubscription(subscriptionId);
        await get().refreshSubscriptions(get().customer?.id || '');
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to cancel subscription.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    reactivateSubscription: async (subscriptionId: string): Promise<void> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        await provider.reactivateSubscription(subscriptionId);
        await get().refreshSubscriptions(get().customer?.id || '');
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to reactivate subscription.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    updateSubscription: async (
      subscriptionId: string,
      params: SubscriptionUpdateParams
    ): Promise<Subscription> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const updatedSubscription = await provider.updateSubscription(
          subscriptionId,
          params
        );
        await get().refreshSubscriptions(get().customer?.id || '');
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
        return updatedSubscription;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to update subscription.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    listSubscriptions: async (
      customerId: string,
      status?: SubscriptionStatus
    ): Promise<Subscription[]> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const subscriptions = await provider.listSubscriptions(
          customerId,
          status
        );
        set((state: PaymentsProviderState) => {
          state.subscriptions = subscriptions;
          state.loading = false;
        });
        return subscriptions;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to list subscriptions.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    retrieveSubscription: async (
      subscriptionId: string
    ): Promise<Subscription | null> => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const subscription =
          await provider.retrieveSubscription(subscriptionId);
        set((state: PaymentsProviderState) => {
          state.loading = false;
        });
        return subscription;
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to retrieve subscription.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
        throw error;
      }
    },

    refreshCustomer: async (customerId: string) => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const customer = await provider.retrieveCustomer(customerId);
        set((state: PaymentsProviderState) => {
          state.customer = customer;
          state.loading = false;
        });
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to refresh customer.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
      }
    },

    refreshSubscriptions: async (
      customerId: string,
      status?: SubscriptionStatus
    ) => {
      set((state: PaymentsProviderState) => {
        state.loading = true;
        state.error = null;
      });
      try {
        if (!get().config) {
          throw new PaymentsError(
            PaymentErrorType.CONFIGURATION_ERROR,
            'Payments provider not initialized. Call initialize() first.'
          );
        }
        const provider = PaymentProviderFactory.create(
          get().config as PaymentsConfig
        );
        const subscriptions = await provider.listSubscriptions(
          customerId,
          status
        );
        const activeSubscription =
          subscriptions.find(
            (sub: Subscription) =>
              sub.status === SubscriptionStatus.ACTIVE ||
              sub.status === SubscriptionStatus.TRIALING
          ) || null;
        set((state: PaymentsProviderState) => {
          state.subscriptions = subscriptions;
          state.activeSubscription = activeSubscription;
          state.loading = false;
        });
      } catch (error: unknown) {
        set((state: PaymentsProviderState) => {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to refresh subscriptions.';
          state.error = new PaymentsError(
            PaymentErrorType.NETWORK_ERROR,
            message
          );
          state.loading = false;
        });
      }
    },

    reset: () => {
      set((state: PaymentsProviderState) => {
        Object.assign(state, initialState);
      });
    },
  }))
);
